import { useCallback, useEffect, useRef, useState } from 'react'

import { loadChildren, searchNodes } from './api'
import { BackendConfig, Id, TreeNode } from './types'

type Dict<V> = Record<string, V>
export const idKey = (id: Id): string => String(id)

export interface UseTreeDataOptions {
	backend: BackendConfig
	isSearchable: boolean
	preloadedData?: Record<string, Array<Record<string, unknown>>> | null
	preloadedActive?: Array<Id> | null
	defaultValue: Id | Array<Id> | null
	defaultTitle: string | Array<string> | null
}

export interface UseTreeDataApi {
	root: Array<TreeNode>
	setRoot: (list: Array<TreeNode>) => void
	nodes: Dict<TreeNode>
	indexNodes: (list: Array<TreeNode>) => void
	expandedIds: Set<string>
	setExpandedIds: React.Dispatch<React.SetStateAction<Set<string>>>
	loadingIds: Set<string>
	ensureChildrenLoaded: (n: TreeNode) => Promise<void>
	pending: boolean
	setPending: (b: boolean) => void
	search: string
	setSearch: (s: string) => void
}

export const useTreeData = (opts: UseTreeDataOptions): UseTreeDataApi => {
	const { backend, isSearchable, preloadedData, preloadedActive, defaultValue } = opts

	const [root, setRoot] = useState<Array<TreeNode>>([])
	const [nodes, setNodes] = useState<Dict<TreeNode>>({})
	const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
	const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set())
	const [pending, setPending] = useState<boolean>(false)
	const [search, setSearch] = useState<string>('')
	const lastSearchTerm = useRef<string>('')

	// Store the initial preloaded state so we can restore it when search is cleared
	const [preloadedRoot, setPreloadedRoot] = useState<Array<TreeNode>>([])
	const [preloadedExpandedIds, setPreloadedExpandedIds] = useState<Set<string>>(new Set())

	const seedExpandable = useCallback((list: Array<TreeNode>): Array<TreeNode> => {
		const walk = (n: TreeNode): TreeNode => ({
			...n,
			hasChildren: true,
			children: n.children ?? [],
		})
		return list.map((n) => walk(n))
	}, [])

	const indexNodes = useCallback((list: Array<TreeNode>) => {
		setNodes((prev) => {
			const next = { ...prev } as Dict<TreeNode>
			const walk = (n: TreeNode) => {
				next[idKey(n.id)] = n
				n.children?.forEach(walk)
			}
			list.forEach(walk)
			return next
		})
	}, [])

	const ensureChildrenLoaded = useCallback(
		async (n: TreeNode): Promise<void> => {
			if (n.loaded) {
				return
			}
			setLoadingIds((s) => new Set(s).add(idKey(n.id)))
			const children = await loadChildren(backend, n.id).finally(() => {
				setLoadingIds((s) => {
					const next = new Set(s)
					next.delete(idKey(n.id))
					return next
				})
			})
			const seededChildren = children.map((c) => ({
				...c,
				hasChildren: true,
				children: c.children ?? [],
				parentId: n.id,
			}))
			n.children = seededChildren
			n.loaded = true
			n.hasChildren = seededChildren.length > 0
			indexNodes(seededChildren)
			setRoot((r) => [...r])
		},
		[backend, indexNodes],
	)

	// Initial preload seeding
	useEffect(() => {
		if (root.length !== 0) {
			return
		}
		if (!preloadedData || Object.keys(preloadedData).length === 0) {
			return
		}

		const normalizeRow = (row: Record<string, unknown>): TreeNode => ({
			id: (row.id ?? row.value ?? row.Id ?? row.ID) as Id,
			title: (row.title ?? row.name ?? row.Title ?? row.NAME) as string,
			hasChildren: true,
			parentId: (row.parentId ?? row.parent_id ?? null) as Id | null,
			loaded: false,
			selected: false,
			children: [],
			sentiment: row.sentiment !== undefined ? (row.sentiment as Sentiment) : undefined,
			sentimentEnabled: row.sentimentEnabled !== undefined ? (row.sentimentEnabled as boolean) : undefined,
		})

		const levels: Record<string, Array<TreeNode>> = {}
		Object.entries(preloadedData).forEach(([k, arr]) => {
			levels[k] = (arr ?? []).map((r) => normalizeRow(r))
		})

		const localIndex: Dict<TreeNode> = {}

		const idx = (n: TreeNode) => (localIndex[idKey(n.id)] = n)

		const seededRoot = (levels.root ?? []).map((n) => {
			n.parentId = null
			idx(n)
			return n
		})

		Object.entries(levels).forEach(([k, arr]) => {
			if (k === 'root') {
				return
			}
			const parent = localIndex[k]
			if (parent) {
				parent.children = arr.map((child) => ({ ...child, parentId: parent.id }))
				parent.loaded = true
				parent.hasChildren = arr.length > 0
				parent.children.forEach((c) => idx(c))
			}
		})

		indexNodes(seededRoot)
		setRoot(seededRoot)

		// Expand along defaults / preloadedActive
		const unionIds = new Map<string, Id>()
		const pushId = (v: Id | null | undefined) => {
			if (v === null || v === undefined) {
				return
			}
			unionIds.set(idKey(v), v)
		}
		if (Array.isArray(defaultValue)) {
			defaultValue.forEach(pushId)
		} else {
			pushId(defaultValue as Id | null)
		}
		if (Array.isArray(preloadedActive)) {
			preloadedActive.forEach(pushId)
		}
		const initialIds = Array.from(unionIds.values())
		const toExpand = new Set<string>()
		initialIds.forEach((id) => {
			let cur = localIndex[idKey(id)]
			const guard = new Set<string>()
			while (cur && cur.parentId !== null && cur.parentId !== undefined) {
				const pk = idKey(cur.parentId)
				if (guard.has(pk)) {
					break
				}
				guard.add(pk)
				toExpand.add(pk)
				cur = localIndex[pk]
			}
		})
		if (toExpand.size > 0) {
			setExpandedIds(new Set<string>(toExpand))
		}

		// Save the preloaded state for restoration when search is cleared
		setPreloadedRoot(seededRoot)
		setPreloadedExpandedIds(new Set<string>(toExpand))
	}, [preloadedData])

	// Root load when opened without preload is left to the caller. Search helper provided below.

	const searchHelper = useCallback(
		async (term: string) => {
			if (!isSearchable) {
				return
			}
			if (term === '') {
				return
			}

			setPending(true)
			try {
				const data = await searchNodes(backend, term)

				if (term !== lastSearchTerm.current.trim()) {
					return
				}

				// 1. Create a local index of nodes with children arrays
				const localIndex: Dict<TreeNode> = {}
				data.forEach((n) => {
					localIndex[idKey(n.id)] = {
						...n,
						children: [],
						loaded: false,
						hasChildren: true,
					}
				})

				// 2. Build the tree and identify roots
				const roots: Array<TreeNode> = []
				const toExpand = new Set<string>()

				Object.values(localIndex).forEach((n) => {
					if (n.parentId && localIndex[idKey(n.parentId)]) {
						localIndex[idKey(n.parentId)].children!.push(n)
						localIndex[idKey(n.parentId)].hasChildren = true
						// If a node has children in the search results, it should be expanded
						toExpand.add(idKey(n.parentId))
					} else {
						roots.push(n)
					}
				})

				setExpandedIds(toExpand)
				indexNodes(roots)
				setRoot(roots)
			} finally {
				setPending(false)
			}
		},
		[backend, indexNodes, isSearchable],
	)

	useEffect(() => {
		const trimmedSearch = search.trim()

		if (trimmedSearch === '') {
			return
		}

		if (trimmedSearch.length < 3) {
			return
		}

		const timer = setTimeout(() => {
			void searchHelper(trimmedSearch)
		}, 400)

		return () => clearTimeout(timer)
	}, [search, searchHelper])

	// expose state and helpers
	return {
		root,
		setRoot,
		nodes,
		indexNodes,
		expandedIds,
		setExpandedIds,
		loadingIds,
		ensureChildrenLoaded,
		pending,
		setPending,
		search,
		setSearch: (s: string) => {
			setSearch(s)
			lastSearchTerm.current = s
			if (s.trim() === '') {
				// Restore preloaded state instead of clearing
				setRoot(preloadedRoot)
				setExpandedIds(preloadedExpandedIds)
				setPending(false)
			}
		},
	}
}
